// Invitation d'un partenaire par la fonction `users` (`partner_invite`, conception du 29/10/2026, § 6) : réponse lue en
// un résultat pour l'écran. Un partenaire déjà lié, ou dont l'e-mail n'est pas parti, reste un partenaire : depuis une
// fiche, on lui partage quand même la fiche.
import { getSupabaseClient } from "../providers/supabase/supabase";

export type InviteOutcome =
  | {
      kind: "linked";
      partnershipId: number;
      created: boolean;
      emailFailed: boolean;
      message: string;
    }
  | { kind: "already"; message: string }
  | { kind: "refused"; message: string };

type InviteResult = {
  data?: { partnership_id: number; created: boolean };
  error?: { code?: string; invited_pending?: boolean; partnership_id?: number };
};

const KEY = "resources.projects.partners.invite.";

export const inviteOutcome = ({ data, error }: InviteResult): InviteOutcome => {
  if (data)
    return {
      kind: "linked",
      partnershipId: data.partnership_id,
      created: data.created,
      emailFailed: false,
      message: KEY + (data.created ? "done_new" : "done_existing"),
    };
  switch (error?.code) {
    case "partnership_exists":
      return {
        kind: "already",
        message: KEY + (error.invited_pending ? "already_pending" : "already"),
      };
    case "invite_email_failed":
      return error.partnership_id != null
        ? {
            kind: "linked",
            partnershipId: error.partnership_id,
            created: true,
            emailFailed: true,
            message: KEY + "email_failed",
          }
        : { kind: "refused", message: KEY + "failed" };
    case "already_member":
    case "not_a_partner":
    case "not_authorized":
      return { kind: "refused", message: KEY + error.code };
    default:
      return { kind: "refused", message: KEY + "failed" };
  }
};

export const invitePartner = async (form: {
  email: string;
  first_name: string;
  last_name: string;
}): Promise<InviteOutcome> => {
  const { data, error } = await getSupabaseClient().functions.invoke<{
    data: { partnership_id: number; created: boolean };
  }>("users", { method: "POST", body: { partner_invite: true, ...form } });
  if (data?.data) return inviteOutcome({ data: data.data });
  let details: NonNullable<InviteResult["error"]> = {};
  try {
    details =
      (await (error as { context?: Response } | null)?.context?.json()) ?? {};
  } catch {
    details = {};
  }
  return inviteOutcome({ error: details });
};

/** Protège `\`, `%` et `_` pour un `ilike` : une adresse porte souvent un `_`, qui désignerait sinon un autre compte. */
export const escapeLike = (value: string) => value.replace(/[\\%_]/g, "\\$&");

/**
 * Compte du partenaire lié. Lien neuf : lu dans `partnerships` par son identifiant (lecture permise à l'hôte) ;
 * partenaire déjà lié : lu dans `sales` par son adresse, caractères spéciaux protégés.
 */
export const partnerSaleIdFor = async (
  outcome: InviteOutcome,
  email: string,
): Promise<number | undefined> => {
  const supabase = getSupabaseClient();
  if (outcome.kind === "linked") {
    const { data } = await supabase
      .from("partnerships")
      .select("partner_sale_id")
      .eq("id", outcome.partnershipId)
      .maybeSingle();
    return data?.partner_sale_id as number | undefined;
  }
  const { data } = await supabase
    .from("sales")
    .select("id")
    .ilike("email", escapeLike(email))
    .maybeSingle();
  return data?.id as number | undefined;
};
